import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchDriverActionsForDate, buildStatusMap } from '../libs/stopStatus';

// Session-level cache of derived stop statuses, keyed by `${date}|${market}`.
// Module-scope Map survives tab switches and re-mounts; page reload clears
// it.
//
// We cache the raw event buckets (the expensive fan-out fetch) but produce
// a fresh Map per consumer call. That lets two tabs request different route
// sets for the same date without re-fetching the events.
const eventBucketsCache = new Map();
const inFlight = new Map();

function cacheKey(date, marketValue) {
  return `${date}|${marketValue || 'all'}`;
}

async function fetchForKey(date, marketValue) {
  const key = cacheKey(date, marketValue);
  if (eventBucketsCache.has(key)) return eventBucketsCache.get(key);
  if (inFlight.has(key)) return inFlight.get(key);

  const promise = fetchDriverActionsForDate(date)
    .then((buckets) => {
      eventBucketsCache.set(key, buckets);
      inFlight.delete(key);
      return buckets;
    })
    .catch((err) => {
      inFlight.delete(key);
      throw err;
    });
  inFlight.set(key, promise);
  return promise;
}

/**
 * Hook: fetch derived stop statuses for one or more stocking dates.
 *
 * @param {object} args
 * @param {Array<string>} args.dates  ISO dates (YYYY-MM-DD); falsy entries skipped.
 * @param {Array<object>} args.routes LocationRoute rows currently displayed.
 *                                    Used to build the per-locationId Map.
 *                                    Status derivation runs against the
 *                                    cached event buckets, not the BE.
 * @param {string} [args.marketValue] BE `market` arg (FE region.value).
 *                                    Included in the cache key.
 * @returns {{
 *   statusesByDate: Object,   // { [date]: Map<locationId, { status, stockedAt, scannedAt, driver }> }
 *   isLoading: boolean,
 *   error: Error|null,
 *   refresh: (dates?: string[]) => void,  // force re-fetch for the given dates
 * }}
 */
export default function useDriverActionStatuses({
  dates,
  routes,
  marketValue,
} = {}) {
  const normalizedDates = (Array.isArray(dates) ? dates : []).filter(Boolean);
  // Stable string key so the effect doesn't re-fire on identical inputs —
  // `normalizedDates` is a fresh array each render and would otherwise
  // trigger the fetch every commit.
  const inputKey = `${normalizedDates.join(',')}|${marketValue || 'all'}`;
  const routesRef = useRef(routes);
  routesRef.current = routes;

  const [statusesByDate, setStatusesByDate] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const buildMapsFromCache = useCallback(
    (datesToBuild) => {
      const next = {};
      const currentRoutes = routesRef.current || [];
      datesToBuild.forEach((d) => {
        const buckets = eventBucketsCache.get(cacheKey(d, marketValue));
        if (buckets) {
          next[d] = buildStatusMap(currentRoutes, buckets);
        }
      });
      return next;
    },
    [marketValue],
  );

  useEffect(() => {
    if (normalizedDates.length === 0) {
      setStatusesByDate({});
      setIsLoading(false);
      setError(null);
      return;
    }

    // Hydrate synchronously from cache so the first render after a tab
    // switch already has populated statuses.
    setStatusesByDate(buildMapsFromCache(normalizedDates));

    const missing = normalizedDates.filter(
      (d) => !eventBucketsCache.has(cacheKey(d, marketValue)),
    );
    if (missing.length === 0) {
      setIsLoading(false);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    Promise.all(missing.map((d) => fetchForKey(d, marketValue)))
      .then(() => {
        if (!mountedRef.current) return;
        setStatusesByDate(buildMapsFromCache(normalizedDates));
      })
      .catch((err) => {
        if (!mountedRef.current) return;
        console.error('[useDriverActionStatuses] fetch failed:', err);
        setError(err);
      })
      .finally(() => {
        if (mountedRef.current) setIsLoading(false);
      });
    // `inputKey` captures (dates, marketValue); `normalizedDates` and
    // `buildMapsFromCache` are derived from those and would cause spurious
    // re-fetches if listed directly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputKey, refreshTick]);

  // Routes can change without the dates changing (e.g. new GetLocationRoutes
  // response). Rebuild the maps from cached buckets when that happens, no
  // network round-trip needed.
  useEffect(() => {
    if (normalizedDates.length === 0) return;
    const fresh = buildMapsFromCache(normalizedDates);
    setStatusesByDate(fresh);
    // Listing `normalizedDates` (fresh array each render) here would cause
    // an infinite update loop; `inputKey` provides the same signal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routes, inputKey]);

  const refresh = useCallback(
    (datesToRefresh) => {
      const list =
        Array.isArray(datesToRefresh) && datesToRefresh.length > 0
          ? datesToRefresh.filter(Boolean)
          : normalizedDates;
      list.forEach((d) => {
        eventBucketsCache.delete(cacheKey(d, marketValue));
      });
      setRefreshTick((t) => t + 1);
    },
    // `normalizedDates` is recreated every render, but its content is
    // captured in `inputKey` for stable identity here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [inputKey, marketValue],
  );

  return { statusesByDate, isLoading, error, refresh };
}

// Test-only helper: clear the module-level cache between specs.
export function resetDriverActionStatusesCacheForTests() {
  eventBucketsCache.clear();
  inFlight.clear();
}
